import { useYahooQuote } from '@/hooks/useYahooQuote';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus, RefreshCw } from 'lucide-react';

interface Props {
  ticker: string;
  className?: string;
  compact?: boolean;
}

export function LivePriceBadge({ ticker, className, compact = false }: Props) {
  const { data: quote, isLoading, error } = useYahooQuote(ticker);

  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-1 text-xs text-muted-foreground', className)}>
        <RefreshCw className="w-3 h-3 animate-spin" />
        <span>Loading…</span>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className={cn('text-xs text-muted-foreground', className)}>
        <span>—</span>
      </div>
    );
  }

  const isUp = quote.change >= 0;
  const isFlat = quote.change === 0;
  const Icon = isFlat ? Minus : isUp ? TrendingUp : TrendingDown;
  const colorClass = isFlat
    ? 'text-muted-foreground'
    : isUp
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-red-500 dark:text-red-400';

  if (compact) {
    return (
      <div className={cn('flex items-center gap-1.5', className)}>
        <span className="font-mono font-semibold text-sm">
          ${quote.price.toFixed(2)}
        </span>
        <span className={cn('text-xs font-medium flex items-center gap-0.5', colorClass)}>
          <Icon className="w-3 h-3" />
          {quote.changePercent >= 0 ? '+' : ''}{quote.changePercent.toFixed(2)}%
        </span>
      </div>
    );
  }

  return (
    <div className={cn('space-y-0.5', className)}>
      <div className="flex items-center gap-2">
        <span className="font-mono font-bold text-base">
          ${quote.price.toFixed(2)}
        </span>
        <span className={cn('text-sm font-medium flex items-center gap-0.5', colorClass)}>
          <Icon className="w-3.5 h-3.5" />
          {quote.change >= 0 ? '+' : ''}{quote.change.toFixed(2)}
          {' '}({quote.changePercent >= 0 ? '+' : ''}{quote.changePercent.toFixed(2)}%)
        </span>
      </div>
      <div className="flex gap-3 text-xs text-muted-foreground">
        <span>Vol: {(quote.volume / 1e6).toFixed(1)}M</span>
        <span>52W: ${quote.fiftyTwoWeekLow.toFixed(0)}–${quote.fiftyTwoWeekHigh.toFixed(0)}</span>
      </div>
    </div>
  );
}
